<script lang="ts" setup>
    import { ref, computed, watch, nextTick } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import Loading from '@shared/components/Loading.vue';
    import { discoverSalesforceObjects, discoverSalesforceObjectFields, type SalesforceObjectSummary, type SalesforceFieldOption, type SalesforceChildRelationship } from './SalesforceObjectConfigurationAssistant.access';
    import { detectLinkCandidates, describeCandidate, type LinkCandidate } from './SalesforceObjectConfigurationAssistant.linking';

    // Interactive builder for SalesforceCredentials.ObjectConfiguration
    // (claude_plans/salesforce-import-plan.md §11) - replaces the old guided
    // import-wizard steps (ContactSource/ChildObjects/Aggregates), which only
    // ever applied to a fresh connection and needed a throwaway first import
    // just to write config. This runs entirely locally: nothing is saved to
    // the server until the Connection form's own Save button is clicked -
    // finishing this assistant only fills in this one field's plain-text
    // value, the same as if it had been hand-typed.
    //
    // Takes the form's current (possibly unsaved) field values directly,
    // since object/field discovery needs real Salesforce credentials before a
    // ConnectionId necessarily exists.
    const props = defineProps<{ allFields: Record<string, string>; modelValue: string }>();
    const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

    type ContactSourcePreset = 'Contact' | 'PersonAccount' | 'Custom';
    type AggregateFunction = 'Latest' | 'Max' | 'Min' | 'Sum' | 'Count';

    interface ChildObjectDraft {
        object: string | null;
        parentField: string | null;
        parentVia: { childObject: string; field: string } | null;
        fields: string[];
        // Both UI-only - never written to the emitted ObjectConfiguration
        // JSON. editing: true while this row is being reconsidered (no
        // resolved link yet, or the person clicked "change manually" on an
        // already-resolved one). rawManual: true drops from the candidate
        // list down to the fully unvalidated object/field pickers - only
        // needed for the rare relationship Salesforce's own field metadata
        // doesn't mark as a lookup (e.g. a text field storing an Id by
        // convention rather than a real reference field).
        editing: boolean;
        rawManual: boolean;
    }

    interface AggregateDraft {
        objectType: string;
        field: string;
        aggregate: AggregateFunction;
        outputColumn: string;
    }

    const AGGREGATE_TYPES: AggregateFunction[] = ['Latest', 'Max', 'Min', 'Sum', 'Count'];

    const open = ref(false);
    const step = ref<0 | 1 | 2 | 3>(0);
    const loading = ref(false);

    const contactSource = ref<ContactSourcePreset>('Contact');
    const contactPredicate = ref('');
    // Only reachable via the 'Custom' preset - Contact/PersonAccount stay
    // fixed presets since that covers every real case seen so far (see
    // claude_plans/salesforce-import-plan.md §11's ContactSource note).
    const customContactObject = ref<string | null>(null);
    const customAccountJoinField = ref<string | null>(null);
    // Which scalar fields (Email, FirstName, ...) get pulled for the contact
    // object - this is what SalesforceSoqlBuilder.BuildContactQuery actually
    // syncs. Without this, only the bare required fields (Id/join field) ever
    // sync, regardless of anything picked in the standard wizard's Fields step
    // (that step saves to the DataSource, not the Connection - see plan §11).
    const contactFields = ref<string[]>([]);
    const syncAccounts = ref(false);
    const accountFields = ref<string[]>([]);
    const syncCampaignMembers = ref(false);
    const campaignMemberFields = ref<string[]>([]);
    // Fields SalesforceUpdateContactAction may write; which one and when is decided in the workflow.
    const writableFields = ref<string[]>([]);
    const showAllWritableFields = ref(false);

    const resolvedContactObject = computed<string | null>(() => {
        if (contactSource.value === 'PersonAccount') return 'Account';
        if (contactSource.value === 'Custom') return customContactObject.value;
        return 'Contact';
    });

    const contactSourceValid = computed(() => !!resolvedContactObject.value);

    // Label + API name, since the workflow needs the API name and labels can collide ("Do Not Contact" vs "Kontakta inte").
    // Already-picked fields stay listed even when the boolean filter would hide them.
    const writableFieldOptions = computed(() =>
        (fieldsByObject.value[resolvedContactObject.value ?? 'Contact'] ?? [])
            .filter((f) => f.Updateable && (showAllWritableFields.value || f.Type === 'boolean' || writableFields.value.includes(f.Name)))
            .map((f) => ({ Name: f.Name, Label: `${f.Label} (${f.Name})` })),
    );

    // loadExisting() below sets contactSource.value while restoring a saved
    // config - that assignment fires this same watcher, since Vue can't
    // distinguish "the user just switched the radio button" from "we're
    // loading what they'd already picked". Without this guard, the watcher's
    // own reset (meant only for the former) wipes contactFields/childObjects
    // back out on every open, right after loadExisting() just restored them -
    // found live: re-opening the assistant on an already-configured
    // connection silently cleared its fields on save.
    let isLoadingExisting = false;

    watch(contactSource, async () => {
        if (isLoadingExisting) return;

        // A field valid for Contact isn't necessarily valid for Account (or a
        // custom object) - carrying a stale pick across a source switch would
        // silently save a field that may not even exist on the new object.
        contactFields.value = [];
        writableFields.value = [];
        if (resolvedContactObject.value) await fieldsFor(resolvedContactObject.value);

        // Person Account isn't a separate queryable object - it's an ordinary
        // Account row with IsPersonAccount = true. Picking this preset alone
        // says nothing about which rows come back; without this filter every
        // Account (real organizations included) would sync as if it were a
        // person. Only set when blank - never overwrite something already typed.
        if (contactSource.value === 'PersonAccount' && !contactPredicate.value) {
            contactPredicate.value = 'IsPersonAccount = true';
        }

        // The reverse case: switching AWAY from Person Account left this
        // auto-set predicate behind. It would still technically run
        // (Contact also exposes IsPersonAccount in a Person-Account-enabled
        // org), just meaning something confusing - only the Contact-shadow
        // records of Person Accounts, not real standalone contacts. Only
        // clear it if it still matches exactly what was auto-set, same rule
        // as above - anything hand-typed is left alone either direction.
        if (contactSource.value !== 'PersonAccount' && contactPredicate.value === 'IsPersonAccount = true') {
            contactPredicate.value = '';
        }

        // "Also sync Accounts" pulls the Account object as the company a
        // Contact belongs to (via AccountId) - meaningless for Person
        // Account, whose own contact source already IS the Account object
        // (BuildAccountQuery has no join, it's a second flat SELECT FROM
        // Account, same rows). Toggle is hidden for this source below; clear
        // it here too so a value set before switching doesn't silently
        // survive as a redundant second fetch of the same object.
        if (contactSource.value === 'PersonAccount') {
            syncAccounts.value = false;
            accountFields.value = [];
        }

        // Every related-record link - auto-detected or manually chosen - was
        // resolved against the OLD contact object. Unlike adding/removing an
        // unrelated object (where only never-touched rows get re-synced, so a
        // manual pick survives untouched), changing the contact source itself
        // invalidates everything: a "field that links to the contact" pick
        // may not even reference the new object at all. Clear unconditionally
        // rather than leave a stale, silently-wrong link in place.
        childObjects.value = childObjects.value.map((row) => ({ ...row, parentField: null, parentVia: null, editing: false, rawManual: false }));
        syncAutoLinks();
    });
    const childObjects = ref<ChildObjectDraft[]>([]);
    const aggregates = ref<AggregateDraft[]>([]);

    const availableObjects = ref<SalesforceObjectSummary[]>([]);
    const fieldsByObject = ref<Record<string, SalesforceFieldOption[]>>({});
    // Objects/fields that point AT the given object - the reverse of
    // ReferenceTo, fetched in the same call as its fields (Salesforce's
    // describe response includes both). Used to suggest real via-sources
    // for an object with no direct link.
    const childRelationshipsByObject = ref<Record<string, SalesforceChildRelationship[]>>({});

    function credentialsPayload() {
        return {
            AuthFlow: props.allFields['AuthFlow'] || 'ClientCredentials',
            InstanceUrl: props.allFields['InstanceUrl'] || '',
            ApiVersion: props.allFields['ApiVersion'] || '',
            ClientId: props.allFields['ClientId'] || '',
            ClientSecret: props.allFields['ClientSecret'] || '',
            Username: props.allFields['Username'] || '',
            Password: props.allFields['Password'] || '',
            PrivateKeyPem: props.allFields['PrivateKeyPem'] || '',
        };
    }

    function loadExisting() {
        if (!props.modelValue) return;
        isLoadingExisting = true;
        try {
            const existing = JSON.parse(props.modelValue);
            const sourceObject = existing.contactSource?.object;
            if (sourceObject === 'Account') {
                contactSource.value = 'PersonAccount';
            } else if (!sourceObject || sourceObject === 'Contact') {
                contactSource.value = 'Contact';
            } else {
                contactSource.value = 'Custom';
                customContactObject.value = sourceObject;
                customAccountJoinField.value = existing.contactSource?.accountJoinField ?? null;
            }
            contactPredicate.value = existing.contactPredicate ?? '';
            contactFields.value = existing.contactFields ?? [];
            syncAccounts.value = existing.syncAccounts ?? false;
            accountFields.value = existing.accountFields ?? [];
            syncCampaignMembers.value = existing.syncCampaignMembers ?? false;
            campaignMemberFields.value = existing.campaignMemberFields ?? [];
            childObjects.value = (existing.childObjects ?? []).map((c: any) => ({
                object: c.object ?? null,
                parentField: c.parentField ?? null,
                parentVia: c.parentVia ? { childObject: c.parentVia.childObject, field: c.parentVia.field } : null,
                fields: c.fields ?? [],
                editing: false,
                rawManual: false,
            }));
            aggregates.value = (existing.contactAggregates ?? []).map((a: any) => ({
                objectType: a.objectType ?? '',
                field: a.field ?? '',
                aggregate: (a.aggregate ?? 'Latest') as AggregateFunction,
                outputColumn: a.outputColumn ?? '',
            }));
            writableFields.value = existing.writableFields ?? [];
        } catch {
            // Not JSON, or an older/hand-typed shape - start fresh rather than block opening.
        } finally {
            // Not reset synchronously here - the contactSource watcher above
            // runs on the next tick, after this function returns, so the
            // flag has to survive until that scheduled run has happened and
            // seen it, or it guards nothing.
            nextTick(() => {
                isLoadingExisting = false;
            });
        }
    }

    async function fieldsFor(objectName: string | null): Promise<SalesforceFieldOption[]> {
        if (!objectName) return [];
        if (!fieldsByObject.value[objectName]) {
            try {
                const describe = await discoverSalesforceObjectFields(objectName, credentialsPayload());
                fieldsByObject.value[objectName] = describe.Fields;
                childRelationshipsByObject.value[objectName] = describe.ChildRelationships;
            } catch {
                bsAlert({ type: AlertTypes.Danger });
                return [];
            }
        }
        return fieldsByObject.value[objectName];
    }

    // Only real navigations - a "field that links to X" picker should never
    // offer a field that can't actually resolve to X (claude_plans/salesforce-
    // import-plan.md §11). Already-fetched fields only; callers fetch first.
    function fieldsReferencing(objectName: string | null, targetObject: string | null): SalesforceFieldOption[] {
        if (!objectName || !targetObject) return [];
        return (fieldsByObject.value[objectName] ?? []).filter((f) => f.ReferenceTo?.includes(targetObject));
    }

    function candidatesFor(index: number): LinkCandidate[] {
        const row = childObjects.value[index];
        if (!row.object || !resolvedContactObject.value) return [];
        // Only an already-resolved row can be a via-source. The backend's
        // ParentVia resolution requires the via object to already have its
        // own ContactSfId - an unresolved row chained through would produce
        // a confident-looking badge that silently links nothing. Found live:
        // "Accepted Event Relation" (itself never configured) was offered as
        // a valid via-source purely because it existed in the list.
        const otherObjects = childObjects.value
            .filter((_, i) => i !== index)
            .filter((r) => r.object && (r.parentField || r.parentVia))
            .map((r) => ({ object: r.object! }));
        return detectLinkCandidates(row.object, resolvedContactObject.value, otherObjects, fieldsByObject.value);
    }

    function describeLinkCandidate(index: number, candidate: LinkCandidate): string {
        const row = childObjects.value[index];
        if (!row.object) return '';
        return describeCandidate(candidate, row.object, availableObjects.value, fieldsByObject.value);
    }

    // Describes whatever's currently set on the row (however it got there -
    // loaded from a saved config, auto-detected, or picked manually), for the
    // resolved badge. Not the same as candidatesFor(), which is about what
    // COULD be detected fresh.
    function resolvedLinkDescription(index: number): string {
        const row = childObjects.value[index];
        if (!row.object) return '';
        const candidate: LinkCandidate | null = row.parentField
            ? { kind: 'direct', field: row.parentField }
            : row.parentVia
              ? { kind: 'via', viaObject: row.parentVia.childObject, field: row.parentVia.field }
              : null;
        return candidate ? describeCandidate(candidate, row.object, availableObjects.value, fieldsByObject.value) : '';
    }

    // Auto-applies a link for any row that isn't already resolved, isn't
    // being manually edited, and has exactly one detected candidate. Rows
    // with zero or multiple candidates are left alone here - the template
    // renders the "couldn't find one" message or the pick-a-candidate list
    // for those, rather than this function guessing. Re-run this whenever
    // the set of objects (or their fetched fields) changes, so entry order
    // never matters - adding the object something connects "through" later
    // retroactively resolves an earlier row that couldn't detect anything yet.
    function syncAutoLinks() {
        childObjects.value = childObjects.value.map((row, index) => {
            if (row.editing || row.parentField || row.parentVia || !row.object || !resolvedContactObject.value) return row;
            const candidates = candidatesFor(index);
            if (candidates.length !== 1) return row;
            const c = candidates[0];
            // c.viaObject -> parentVia.childObject is not a rename, it's the
            // actual mapping - see ViaLinkCandidate's doc comment in
            // SalesforceObjectConfigurationAssistant.linking.ts before
            // touching this line. viaObject is the object whose OWN field
            // points at this row's object, not the reverse.
            return {
                ...row,
                parentField: c.kind === 'direct' ? c.field : null,
                parentVia: c.kind === 'via' ? { childObject: c.viaObject, field: c.field } : null,
            };
        });
    }

    // Resolves an ambiguous row (multiple candidates) by picking one - still
    // came from detection, not hand-typed, so this doesn't need to enter the
    // manual editor. Same viaObject -> parentVia.childObject mapping as
    // syncAutoLinks() above - see ViaLinkCandidate's doc comment before
    // changing it.
    function pickCandidate(index: number, candidate: LinkCandidate) {
        updateChildObject(
            index,
            candidate.kind === 'direct'
                ? { parentField: candidate.field, parentVia: null, editing: false, rawManual: false }
                : { parentField: null, parentVia: { childObject: candidate.viaObject, field: candidate.field }, editing: false, rawManual: false }
        );
    }

    function finishEditing(index: number) {
        updateChildObject(index, { editing: false, rawManual: false });
        syncAutoLinks();
    }

    function onToggleSyncAccounts(checked: boolean) {
        syncAccounts.value = checked;
        if (checked) fieldsFor('Account');
    }

    function onToggleSyncCampaignMembers(checked: boolean) {
        syncCampaignMembers.value = checked;
        if (checked) fieldsFor('CampaignMember');
    }

    async function onCustomContactObjectPicked(objectName: string | null) {
        customContactObject.value = objectName;
        customAccountJoinField.value = null;
        contactFields.value = [];
        writableFields.value = [];
        await fieldsFor(objectName);
    }

    async function openAssistant() {
        loadExisting();
        step.value = 0;
        open.value = true;
        loading.value = true;
        try {
            availableObjects.value = await discoverSalesforceObjects(credentialsPayload());
            if (resolvedContactObject.value) await fieldsFor(resolvedContactObject.value);
            if (syncAccounts.value) await fieldsFor('Account');
            if (syncCampaignMembers.value) await fieldsFor('CampaignMember');
            // Needed both for a loaded config's badges to show real Labels
            // (not a raw API name fallback) and so syncAutoLinks() has
            // something to detect against for any row missing a link.
            for (const row of childObjects.value) {
                if (row.object) await fieldsFor(row.object);
            }
            syncAutoLinks();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            loading.value = false;
        }
    }

    function closeAssistant() {
        open.value = false;
    }

    function addChildObject() {
        childObjects.value = [...childObjects.value, { object: null, parentField: null, parentVia: null, fields: [], editing: false, rawManual: false }];
    }

    function removeChildObject(index: number) {
        const removedObject = childObjects.value[index]?.object;
        const remaining = childObjects.value.filter((_, i) => i !== index);
        childObjects.value = remaining.map((row) =>
            row.parentVia?.childObject === removedObject ? { ...row, parentField: null, parentVia: null, editing: false, rawManual: false } : row
        );
        syncAutoLinks();
    }

    function updateChildObject(index: number, patch: Partial<ChildObjectDraft>) {
        childObjects.value = childObjects.value.map((row, i) => (i === index ? { ...row, ...patch } : row));
    }

    async function onChildObjectPicked(index: number, objectName: string | null) {
        updateChildObject(index, { object: objectName, fields: [], parentField: null, parentVia: null, editing: false, rawManual: false });
        await fieldsFor(objectName);
        syncAutoLinks();
    }

    function otherConfiguredChildObjects(index: number) {
        return childObjects.value.map((row, i) => ({ row, i })).filter(({ row, i }) => i !== index && row.object);
    }

    // Resolved here rather than inline in the template - a literal '}}'
    // inside a template {{ }} mustache expression breaks Vue's compiler
    // (it scans for the first '}}' to end the expression, not JS-aware of
    // string boundaries), so the {{list}}-style placeholder has to be
    // substituted in script and bound as an already-resolved string.
    function noMatchingViaFieldText(index: number): string {
        const names = otherConfiguredChildObjects(index)
            .map(({ row: r }) => objectLabel(r.object!))
            .join(', ');
        return $Resources.SalesforceAssistantNoMatchingViaFieldText.replace('{{list}}', names);
    }

    // Same list as otherConfiguredChildObjects, narrowed to objects that
    // actually have a field pointing at this row's object - the manual
    // editor's "Linked through" picker used to list every already-added
    // object unconditionally, letting someone pick one with no real
    // relationship and only discover that later when "Field on that object"
    // came up empty. This applies the same ReferenceTo check upfront that
    // the auto-detector already uses - plus requiring the via-source to
    // already be resolved itself (same fix as candidatesFor() above; a via
    // chain through an unresolved object silently links nothing at sync
    // time, regardless of whether it was picked automatically or by hand).
    function validViaObjects(index: number) {
        const row = childObjects.value[index];
        if (!row.object) return [];
        return otherConfiguredChildObjects(index).filter(
            ({ row: other }) => other.object && (other.parentField || other.parentVia) && fieldsReferencing(other.object, row.object!).length > 0
        );
    }

    // Related objects that DO have a field pointing at this row, but aren't
    // themselves resolved yet - the more useful message when this is why
    // validViaObjects() came up empty (fix that object first), versus "no
    // real relationship exists at all".
    function referencingButUnresolved(index: number) {
        const row = childObjects.value[index];
        if (!row.object) return [];
        return otherConfiguredChildObjects(index).filter(
            ({ row: other }) => other.object && !(other.parentField || other.parentVia) && fieldsReferencing(other.object, row.object!).length > 0
        );
    }

    // Salesforce Label, never the raw API name - same rule as everywhere
    // else in this assistant (see SalesforceObjectConfigurationAssistant.linking.ts).
    function objectLabel(objectName: string): string {
        return availableObjects.value.find((o) => o.Name === objectName)?.Label ?? objectName;
    }

    // Real via-source candidates for the zero-candidates case: every object
    // that has a field pointing AT this one (Salesforce's own
    // childRelationships, fetched in the same call as this object's own
    // fields - no extra API call), excluding whatever's already in the list
    // (those are covered by the real candidate mechanism already). Unlike an
    // outbound-reference hint, this is the actual direction the via
    // mechanism needs - adding one of these and resolving IT is a real path,
    // not a guess.
    function suggestedViaSourceLabels(index: number): string[] {
        const row = childObjects.value[index];
        if (!row.object) return [];
        const alreadyAdded = new Set(childObjects.value.map((r) => r.object).filter((o): o is string => !!o));
        const targets = new Set<string>();
        for (const cr of childRelationshipsByObject.value[row.object] ?? []) {
            if (!alreadyAdded.has(cr.ChildObject)) targets.add(cr.ChildObject);
        }
        return [...targets].map(objectLabel);
    }

    function addAggregate() {
        aggregates.value = [...aggregates.value, { objectType: childObjects.value[0]?.object ?? '', field: '', aggregate: 'Latest', outputColumn: '' }];
    }

    function removeAggregate(index: number) {
        const next = [...aggregates.value];
        next.splice(index, 1);
        aggregates.value = next;
    }

    function updateAggregate(index: number, patch: Partial<AggregateDraft>) {
        aggregates.value = aggregates.value.map((row, i) => (i === index ? { ...row, ...patch } : row));
    }

    function contactSourceConfig() {
        if (contactSource.value === 'PersonAccount') return { object: 'Account', accountJoinField: null };
        if (contactSource.value === 'Custom') return { object: customContactObject.value, accountJoinField: customAccountJoinField.value || null };
        return { object: 'Contact', accountJoinField: 'AccountId' };
    }

    function finish() {
        const config = {
            contactSource: contactSourceConfig(),
            contactPredicate: contactPredicate.value || null,
            contactFields: contactFields.value,
            syncAccounts: syncAccounts.value,
            accountFields: accountFields.value,
            syncCampaignMembers: syncCampaignMembers.value,
            campaignMemberFields: campaignMemberFields.value,
            childObjects: childObjects.value
                .filter((c) => c.object)
                .map((c) => ({
                    object: c.object,
                    parentField: c.parentVia ? null : c.parentField,
                    parentVia: c.parentVia,
                    fields: c.fields,
                })),
            contactAggregates: aggregates.value
                .filter((a) => a.objectType && a.outputColumn && (a.aggregate === 'Count' || a.field))
                .map((a) => ({ objectType: a.objectType, field: a.aggregate === 'Count' ? null : a.field, aggregate: a.aggregate, outputColumn: a.outputColumn })),
            writableFields: writableFields.value,
        };

        emit('update:modelValue', JSON.stringify(config));
        open.value = false;
    }
</script>

<template>
    <div class="m-sm-t">
        <button type="button" class="btn btn-secondary btn-sm" @click="openAssistant">{{ $Resources.SalesforceAssistantOpenButton }}</button>

        <Modal :show="open" :title="$Resources.SalesforceAssistantModalTitle" size="lg" @close="closeAssistant">
            <template #content>
                <Loading :show="loading" />

                <template v-if="!loading">
                    <template v-if="step === 0">
                        <h4>{{ $Resources.SalesforceAssistantStep0Heading }}</h4>
                        <p class="text-muted">{{ $Resources.SalesforceAssistantStep0Description }}</p>

                        <div class="form-group">
                            <FormRadioGroup
                                :legend="null"
                                :options="[
                                    { label: $Resources.SalesforceAssistantContactSourceContactLabel, value: 'Contact' },
                                    { label: $Resources.SalesforceAssistantContactSourcePersonAccountLabel, value: 'PersonAccount' },
                                    { label: $Resources.SalesforceAssistantContactSourceCustomLabel, value: 'Custom' },
                                ]"
                                v-model="contactSource" />
                        </div>

                        <template v-if="contactSource === 'Custom'">
                            <div class="form-group">
                                <MultiSelect
                                    :items="availableObjects"
                                    selectProp="Name"
                                    displayProp="Label"
                                    selectOnly="Name"
                                    :label="$Resources.SalesforceAssistantObjectLabel"
                                    :maxSelectable="1"
                                    :selectedItems="customContactObject ? [customContactObject] : []"
                                    @update:selectedItems="(val) => onCustomContactObjectPicked(val[0] ?? null)" />
                            </div>

                            <div class="form-group" v-if="customContactObject">
                                <MultiSelect
                                    :items="fieldsReferencing(customContactObject, 'Account')"
                                    selectProp="Name"
                                    displayProp="Label"
                                    selectOnly="Name"
                                    :label="$Resources.SalesforceAssistantAccountJoinFieldLabel"
                                    :maxSelectable="1"
                                    :selectedItems="customAccountJoinField ? [customAccountJoinField] : []"
                                    @update:selectedItems="(val) => (customAccountJoinField = val[0] ?? null)" />
                            </div>
                        </template>

                        <FormInput
                            class="form-group"
                            :label="$Resources.SalesforceAssistantContactPredicateLabel"
                            :placeholder="$Resources.SalesforceAssistantContactPredicatePlaceholder"
                            v-model="contactPredicate" />

                        <div class="form-group" v-if="resolvedContactObject">
                            <MultiSelect
                                :items="fieldsByObject[resolvedContactObject] ?? []"
                                selectProp="Name"
                                displayProp="Label"
                                selectOnly="Name"
                                :label="$Resources.SalesforceAssistantContactFieldsLabel"
                                :selectedItems="contactFields"
                                @update:selectedItems="(val) => (contactFields = val)" />
                        </div>

                        <template v-if="contactSource !== 'PersonAccount'">
                            <div class="form-group">
                                <FormCheckable :label="$Resources.SalesforceAssistantSyncAccountsLabel" :checked="syncAccounts" @update:modelValue="onToggleSyncAccounts" />
                            </div>

                            <div class="form-group" v-if="syncAccounts">
                                <MultiSelect
                                    :items="fieldsByObject['Account'] ?? []"
                                    selectProp="Name"
                                    displayProp="Label"
                                    selectOnly="Name"
                                    :label="$Resources.SalesforceAssistantAccountFieldsLabel"
                                    :selectedItems="accountFields"
                                    @update:selectedItems="(val) => (accountFields = val)" />
                            </div>
                        </template>

                        <div class="form-group">
                            <FormCheckable
                                :label="$Resources.SalesforceAssistantSyncCampaignMembersLabel"
                                :checked="syncCampaignMembers"
                                @update:modelValue="onToggleSyncCampaignMembers" />
                        </div>

                        <div class="form-group" v-if="syncCampaignMembers">
                            <MultiSelect
                                :items="fieldsByObject['CampaignMember'] ?? []"
                                selectProp="Name"
                                displayProp="Label"
                                selectOnly="Name"
                                :label="$Resources.SalesforceAssistantCampaignMemberFieldsLabel"
                                :selectedItems="campaignMemberFields"
                                @update:selectedItems="(val) => (campaignMemberFields = val)" />
                        </div>
                    </template>

                    <template v-else-if="step === 1">
                        <h4>{{ $Resources.SalesforceAssistantStep1Heading }}</h4>
                        <p class="text-muted">{{ $Resources.SalesforceAssistantStep1Description }}</p>

                        <div v-for="(row, index) in childObjects" :key="index" class="card m-lg-b p-md">
                            <div class="form-group">
                                <MultiSelect
                                    :items="availableObjects"
                                    selectProp="Name"
                                    displayProp="Label"
                                    selectOnly="Name"
                                    :label="$Resources.SalesforceAssistantObjectLabel"
                                    :maxSelectable="1"
                                    :selectedItems="row.object ? [row.object] : []"
                                    @update:selectedItems="(val) => onChildObjectPicked(index, val[0] ?? null)" />
                            </div>

                            <template v-if="row.object">
                                <div class="form-group">
                                    <MultiSelect
                                        :items="fieldsByObject[row.object] ?? []"
                                        selectProp="Name"
                                        displayProp="Label"
                                        selectOnly="Name"
                                        :label="$Resources.SalesforceAssistantChildFieldsLabel"
                                        :selectedItems="row.fields"
                                        @update:selectedItems="(val) => updateChildObject(index, { fields: val })" />
                                </div>

                                <!-- Resolved: either auto-detected or already configured (loaded/manual) -->
                                <div v-if="!row.editing && (row.parentField || row.parentVia)" class="form-group">
                                    <BsAlert type="success" :dismissible="false" :content="'🔗 ' + resolvedLinkDescription(index)">
                                        <template #button>
                                            <a href="#" @click.prevent="updateChildObject(index, { editing: true })">{{ $Resources.SalesforceAssistantChangeManuallyLink }}</a>
                                        </template>
                                    </BsAlert>
                                </div>

                                <!-- Not resolved yet, or explicitly being reconsidered -->
                                <template v-else>
                                    <!-- Candidate list first - whatever's actually validated against real Salesforce metadata -->
                                    <div v-if="!row.rawManual && candidatesFor(index).length > 0" class="form-group">
                                        <p class="text-muted m-sm-b">{{ $Resources.SalesforceAssistantPickConnectionText }}</p>
                                        <div v-for="(candidate, cIndex) in candidatesFor(index)" :key="cIndex" class="m-sm-b">
                                            <a href="#" @click.prevent="pickCandidate(index, candidate)">{{ describeLinkCandidate(index, candidate) }}</a>
                                        </div>
                                        <a href="#" @click.prevent="updateChildObject(index, { rawManual: true })">{{ $Resources.SalesforceAssistantConfigureManuallyOptionLink }}</a>
                                    </div>

                                    <!-- Nothing found automatically -->
                                    <div v-else-if="!row.rawManual" class="form-group">
                                        <p class="text-muted">
                                            {{ $Resources.SalesforceAssistantNoConnectionFoundText }}
                                            <a href="#" @click.prevent="updateChildObject(index, { rawManual: true })">{{ $Resources.SalesforceAssistantConfigureManuallyInlineLink }}</a>.
                                        </p>
                                        <p v-if="suggestedViaSourceLabels(index).length > 0" class="text-muted">
                                            {{ $Resources.SalesforceAssistantSuggestedViaSourcesText }} {{ suggestedViaSourceLabels(index).join(', ') }}.
                                        </p>
                                    </div>

                                    <!-- Fully manual - unvalidated pickers, a deliberate last resort now, not the default -->
                                    <template v-else>
                                        <div class="form-group">
                                            <label class="m-md-r">
                                                <input type="radio" :checked="!row.parentVia" @change="updateChildObject(index, { parentVia: null })" />
                                                {{ $Resources.SalesforceAssistantLinkedDirectlyLabel }}
                                            </label>
                                            <label>
                                                <input
                                                    type="radio"
                                                    :checked="!!row.parentVia"
                                                    :disabled="validViaObjects(index).length === 0"
                                                    @change="updateChildObject(index, { parentVia: { childObject: '', field: '' }, parentField: null })" />
                                                {{ $Resources.SalesforceAssistantLinkedThroughLabel }}
                                            </label>
                                        </div>

                                        <p v-if="validViaObjects(index).length === 0" class="text-muted">
                                            <template v-if="otherConfiguredChildObjects(index).length === 0">
                                                {{ $Resources.SalesforceAssistantNoViaObjectsText }}
                                            </template>
                                            <template v-else-if="referencingButUnresolved(index).length > 0">
                                                {{
                                                    referencingButUnresolved(index)
                                                        .map(({ row: r }) => objectLabel(r.object!))
                                                        .join(', ')
                                                }}
                                                {{ $Resources.SalesforceAssistantUnresolvedViaSourceText }}
                                            </template>
                                            <template v-else>
                                                {{ noMatchingViaFieldText(index) }}
                                            </template>
                                        </p>

                                        <div v-if="!row.parentVia" class="form-group">
                                            <MultiSelect
                                                :items="fieldsReferencing(row.object, resolvedContactObject)"
                                                selectProp="Name"
                                                displayProp="Label"
                                                selectOnly="Name"
                                                :label="$Resources.SalesforceAssistantParentFieldLabel"
                                                :maxSelectable="1"
                                                :selectedItems="row.parentField ? [row.parentField] : []"
                                                @update:selectedItems="(val) => updateChildObject(index, { parentField: val[0] ?? null })" />
                                        </div>

                                        <template v-else>
                                            <div class="form-group">
                                                <MultiSelect
                                                    :items="validViaObjects(index).map(({ row: r }) => ({ Name: r.object, Label: objectLabel(r.object!) }))"
                                                    selectProp="Name"
                                                    displayProp="Label"
                                                    selectOnly="Name"
                                                    :label="$Resources.SalesforceAssistantLinkedThroughFieldLabel"
                                                    :maxSelectable="1"
                                                    :selectedItems="row.parentVia?.childObject ? [row.parentVia.childObject] : []"
                                                    @update:selectedItems="(val) => updateChildObject(index, { parentVia: { childObject: val[0] ?? '', field: '' } })" />
                                            </div>
                                            <div v-if="row.parentVia?.childObject" class="form-group">
                                                <MultiSelect
                                                    :items="fieldsReferencing(row.parentVia.childObject, row.object)"
                                                    selectProp="Name"
                                                    displayProp="Label"
                                                    selectOnly="Name"
                                                    :label="$Resources.SalesforceAssistantViaFieldLabel"
                                                    :maxSelectable="1"
                                                    :selectedItems="row.parentVia.field ? [row.parentVia.field] : []"
                                                    @update:selectedItems="
                                                        (val) => updateChildObject(index, { parentVia: { childObject: row.parentVia!.childObject, field: val[0] ?? '' } })
                                                    " />
                                            </div>
                                        </template>
                                    </template>

                                    <button type="button" class="btn btn-link" @click="finishEditing(index)">{{ $Resources.SalesforceAssistantFinishEditingButton }}</button>
                                </template>
                            </template>

                            <button type="button" class="btn btn-link" @click="removeChildObject(index)">{{ $Resources.SalesforceAssistantRemoveButton }}</button>
                        </div>

                        <button type="button" class="btn btn-secondary" @click="addChildObject">{{ $Resources.SalesforceAssistantAddChildObjectButton }}</button>
                    </template>

                    <template v-else-if="step === 2">
                        <h4>{{ $Resources.SalesforceAssistantStep2Heading }}</h4>
                        <p class="text-muted">{{ $Resources.SalesforceAssistantStep2Description }}</p>

                        <p v-if="childObjects.filter((c) => c.object).length === 0" class="text-muted">
                            {{ $Resources.SalesforceAssistantNoChildObjectsForAggregateText }}
                        </p>

                        <template v-else>
                            <div v-for="(row, index) in aggregates" :key="index" class="card m-lg-b p-md">
                                <div class="form-group">
                                    <MultiSelect
                                        :items="childObjects.filter((c) => c.object).map((c) => ({ Name: c.object, Label: objectLabel(c.object!) }))"
                                        selectProp="Name"
                                        displayProp="Label"
                                        selectOnly="Name"
                                        :label="$Resources.SalesforceAssistantObjectLabel"
                                        :maxSelectable="1"
                                        :selectedItems="row.objectType ? [row.objectType] : []"
                                        @update:selectedItems="(val) => updateAggregate(index, { objectType: val[0] ?? '', field: '' })" />
                                </div>

                                <div class="form-group">
                                    <FormSelect
                                        :label="$Resources.SalesforceAssistantAggregateTypeLabel"
                                        :modelValue="row.aggregate"
                                        :options="AGGREGATE_TYPES.map((t) => ({ label: t, value: t }))"
                                        @update:modelValue="(newVal: string) => updateAggregate(index, { aggregate: newVal as AggregateFunction })" />
                                </div>

                                <div class="form-group" v-if="row.aggregate !== 'Count'">
                                    <MultiSelect
                                        :items="fieldsByObject[row.objectType] ?? []"
                                        selectProp="Name"
                                        displayProp="Label"
                                        selectOnly="Name"
                                        :label="$Resources.SalesforceAssistantAggregateFieldLabel"
                                        :maxSelectable="1"
                                        :selectedItems="row.field ? [row.field] : []"
                                        @update:selectedItems="(val) => updateAggregate(index, { field: val[0] ?? '' })" />
                                </div>

                                <FormInput class="form-group" :label="$Resources.SalesforceAssistantOutputColumnLabel" v-model="row.outputColumn" />

                                <button type="button" class="btn btn-link" @click="removeAggregate(index)">{{ $Resources.SalesforceAssistantRemoveButton }}</button>
                            </div>

                            <button type="button" class="btn btn-secondary" @click="addAggregate">{{ $Resources.SalesforceAssistantAddAggregateButton }}</button>
                        </template>
                    </template>

                    <template v-else-if="step === 3">
                        <h4>{{ $Resources.SalesforceAssistantWritableFieldsHeading }}</h4>
                        <p class="text-muted">{{ $Resources.SalesforceAssistantWritableFieldsDescription }}</p>

                        <div class="form-group">
                            <FormCheckable
                                :label="$Resources.SalesforceAssistantWritableFieldsShowAllLabel"
                                :checked="showAllWritableFields"
                                @update:modelValue="(val: boolean) => (showAllWritableFields = val)" />
                        </div>

                        <div class="form-group">
                            <MultiSelect
                                :items="writableFieldOptions"
                                selectProp="Name"
                                displayProp="Label"
                                selectOnly="Name"
                                :label="$Resources.SalesforceAssistantWritableFieldsLabel"
                                :selectedItems="writableFields"
                                @update:selectedItems="(val) => (writableFields = val)" />
                        </div>
                    </template>
                </template>
            </template>

            <template #button>
                <button type="button" class="btn btn-default" v-if="step > 0" @click="step = (step - 1) as 0 | 1 | 2 | 3">{{ $Resources.SalesforceAssistantBackButton }}</button>
                <button type="button" class="btn btn-primary" v-if="step < 3" :disabled="step === 0 && !contactSourceValid" @click="step = (step + 1) as 0 | 1 | 2 | 3">{{ $Resources.SalesforceAssistantNextButton }}</button>
                <button type="button" class="btn btn-primary" v-else :disabled="!contactSourceValid" @click="finish">{{ $Resources.SalesforceAssistantDoneButton }}</button>
            </template>
        </Modal>
    </div>
</template>
